import { ScrollArea } from "../../components/ui/scroll-area";
import { Button } from "../../components/ui/button";
import { useInitialVisitAvailability } from "../../hooks/useInitialVisitAvailability";
import { useInitialVisitStore } from "../../stores/initialVisitStore";
import { DateTime } from "luxon";
import { Skeleton } from "../../components/ui/skeleton";
import { useMemo } from "react";
import { Card, CardContent } from "../../components/ui/card";
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  ClipboardClockIcon
} from "lucide-react";

export function SlotsSection() {
  const {
    date,
    uniqueId,
    selectedProvider,
    providerOptions,
    setSelectedSlot,
    setWaitlist
  } = useInitialVisitStore();

  const { data, isLoading } = useInitialVisitAvailability({
    uniqueId: uniqueId,
    enabled: Boolean(uniqueId)
  });

  const timeSlots = useMemo(() => {
    const slots: {
      [key: string]: {
        [key: string]: {
          [key: string]: { startISO: string; endISO: string };
        };
      };
    } = data?.availability;

    if (!slots) return {};
    return slots;
  }, [data, date]);

  const filteredProviderSlots = useMemo(() => {
    const dateKey = date.toFormat("yyyy-MM-dd");
    if (!timeSlots) return [];

    const isTodaySelected = date.hasSame(DateTime.now(), "day");
    const nowPlusTwoHours = DateTime.now().plus({ hours: 2 });

    const providerIds =
      selectedProvider.value === "all"
        ? Object.keys(timeSlots)
        : [selectedProvider.value];

    const grouped: {
      [timeLabel: string]: {
        start: DateTime;
        providers: { providerId: string; label: string; end: DateTime }[];
      };
    } = {};

    providerIds.forEach((providerId) => {
      const daySlots = (timeSlots as any)[providerId]?.[dateKey];
      if (!daySlots || !Array.isArray(daySlots)) return;

      daySlots.forEach((slot: { startISO: string; endISO: string }) => {
        const start = DateTime.fromISO(slot.startISO);
        const end = DateTime.fromISO(slot.endISO);

        // If booking for today, only show slots at least 2 hours from now
        if (isTodaySelected && start.toMillis() < nowPlusTwoHours.toMillis()) {
          return;
        }
        const timeLabel = start.toFormat("h:mm a");
        const label =
          providerOptions.find((option) => option.value === providerId)
            ?.label || providerId;

        if (!grouped[timeLabel]) {
          grouped[timeLabel] = { start, providers: [] };
        }
        grouped[timeLabel].providers.push({ providerId, label, end });
      });
    });

    return Object.entries(grouped)
      .sort((a, b) => a[1].start.toMillis() - b[1].start.toMillis())
      .map(([label, { start, providers }]) => ({ label, start, providers }));
  }, [date, selectedProvider, timeSlots, providerOptions]);

  return (
    <div>
      <div className="flex items-center justify-between p-6">
        <div className="flex gap-1 items-center">
          <p className="text-base font-semibold">{date.toFormat("cccc")}</p>
          <p className="text-base text-muted-foreground">
            {date.toFormat("d")}
          </p>
        </div>
        <Button size="sm" className="gap-1" onClick={() => setWaitlist(true)}>
          Join waitlist
          <ArrowUpRightIcon />
        </Button>
      </div>
      <ScrollArea className="h-full lg:h-96 px-6 mr-0.5">
        <div className="grid grid-cols-1 gap-1.5 w-full lg:w-60 py-2">
          {isLoading &&
            Array.from({ length: 10 }).map((_, index) => (
              <Skeleton key={index} className="w-full h-9 rounded-md" />
            ))}
          {!isLoading &&
            filteredProviderSlots.length > 0 &&
            filteredProviderSlots.map(({ label, start, providers }: any) => (
              <Card
                key={label}
                className="p-0 border-none shadow-none dark:bg-transparent"
              >
                <CardContent className="px-0 py-1 space-y-2">
                  <h3 className="text-sm font-medium">{label}</h3>
                  <div className="flex flex-col gap-1">
                    {providers.map(
                      ({ providerId, label: providerLabel, end }: any) => (
                        <Button
                          key={`${label}-${providerId}`}
                          variant="outline"
                          size="sm"
                          className="w-full dark:bg-accent dark:hover:bg-muted"
                          onClick={() =>
                            setSelectedSlot({
                              uniqueId: uniqueId || "",
                              start: start,
                              end: end,
                              providerId: providerId
                            })
                          }
                        >
                          {providerLabel}
                        </Button>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          {!isLoading && filteredProviderSlots.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No availability for this date
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
